import React, { useEffect, useState } from 'react'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import { createTicketTransfer } from '../../utilities/api'
import { emailPatternMatch } from '../../utilities/helpers'
import authService from '../../utilities/services/auth.service'
import { Spinner } from '../LoadingContainer/Spinner'

export default function AccessPassTransferModal({
  accessPass,
  show,
  onHide,
  onTransferred,
}) {
  const [email, setEmail] = useState('')
  const [step, setStep] = useState('email')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const userEmail = authService.getUser()?.user?.email || ''
  const normalizedEmail = email.trim().toLowerCase()
  const isSameEmail =
    normalizedEmail && normalizedEmail === userEmail.trim().toLowerCase()
  const isValidEmail = emailPatternMatch(normalizedEmail)

  useEffect(() => {
    if (!show) {
      setEmail('')
      setStep('email')
      setIsSaving(false)
      setError('')
    }
  }, [show])

  const submit = async () => {
    if (!isValidEmail || isSameEmail) return

    if (step === 'email') {
      setStep('confirm')
      return
    }

    setIsSaving(true)
    setError('')
    try {
      await createTicketTransfer({
        accessPassId: accessPass.uuid,
        email: normalizedEmail,
      })
      setStep('success')
      onTransferred?.(accessPass.uuid)
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data ||
          'Unable to transfer this access pass. Please try again.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={step !== 'success'}>
        <Modal.Title as="h5">Transfer access pass</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'email' && (
          <Form
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <p>
              Enter the email address of the person receiving{' '}
              <strong>{accessPass?.name || 'this access pass'}</strong>.
            </p>
            <Form.Group controlId={`access-pass-transfer-${accessPass?.uuid}`}>
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="mail@example.com"
                isInvalid={Boolean(email) && (!isValidEmail || isSameEmail)}
                autoFocus
                required
              />
              <Form.Control.Feedback type="invalid">
                {isSameEmail
                  ? 'This access pass is already assigned to your email.'
                  : 'Enter a valid email address.'}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        )}

        {step === 'confirm' && (
          <>
            <p>
              Transfer <strong>{accessPass?.name || 'this access pass'}</strong>{' '}
              to <strong>{normalizedEmail}</strong>?
            </p>
            <p className="small text-muted mb-0">
              The pass will be unavailable until the recipient accepts or you
              cancel the transfer.
            </p>
          </>
        )}

        {step === 'success' && (
          <Alert variant="success" className="mb-0">
            The access pass transfer is pending. You can manage it from My
            transfers until the recipient accepts it.
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {String(error)}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'confirm' && (
          <Button variant="outline-light" onClick={() => setStep('email')}>
            Back
          </Button>
        )}
        {step === 'success' ? (
          <Button onClick={onHide}>Close</Button>
        ) : (
          <Stack direction="horizontal" gap={2}>
            <Button variant="outline-light" onClick={onHide}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!isValidEmail || isSameEmail || isSaving}
            >
              {isSaving ? (
                <Spinner />
              ) : step === 'confirm' ? (
                'Transfer'
              ) : (
                'Next'
              )}
            </Button>
          </Stack>
        )}
      </Modal.Footer>
    </Modal>
  )
}
